import React, { useEffect, useState, useRef, useContext } from "react";
import { connect } from "react-redux";
import { DATA_TYPE_DATE_TIME, DATA_TYPE_DATETIME_WITHDOW, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CONTENT_LIBRARY_EDIT } from "typeConstants/rights";
import { createTopic, retrieveTopicMarkdown, retrieveTopicMarkup, updateTopic, updateTopicMarkdown, promoteTopic, listFormsUsingTopic, deleteTopic } from "requests/cms";
import { InitialState } from "reducers/initialState";
import { Button, MCard } from "components/Shared";
import { KeyValueOriginal, MCardAction, MCardControlMethods, MCardField } from "components/Shared/MCard";
import { phoenixProjectId } from "./ContentManagementList";
import { MarkdownResourceResponse, TopicRequest, TopicResponse, SimpleResult } from "requests/cms/interfaces";
import LoadingCover from "components/Shared/LoadingCover";
import MarkdownEditor, { useMarkdownEditor } from "components/Shared/MarkdownEditor";
import ResourceManager from "./ResourceManager";
import * as yup from "yup";
import { DialogProviderValue, DialogContext } from "components/Shared/DialogProvider";
import { PhoenixFormListItem } from "interfaces/Mueller.Business.Models";
import MGrid, { MGridColumnDefinition } from "components/Shared/MGrid";
import responseHandler from "responseHandler";

interface Props {
  topic: TopicResponse,
  resources: MarkdownResourceResponse[],
  isCreating: boolean,
  formsList: PhoenixFormListItem[],
  onMarkdownEditChanged: (isEditing: boolean) => void,
  onCreateFinished: (response: TopicResponse) => void,
  refreshList: () => void,
  refreshResources: () => void,

  // redux
  canEditContent: boolean,
  makeToast: (text: string, type: string) => void,
};

export const getUniqueKey = (name: string) => name.toLowerCase().replace(/\s+/g, '_');

const ContentPane = ({
  topic,
  resources,
  isCreating,
  formsList,
  onMarkdownEditChanged,
  onCreateFinished,
  refreshList,
  refreshResources,
  canEditContent,
  makeToast,
}: Props) => {
  const mounted = useRef(true);
  const creatingControlMethods = useRef<MCardControlMethods>();
  const [topicMarkdown, setTopicMarkdown] = useState<string>();
  const [topicMarkup, setTopicMarkup] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [referencedForms, setReferencedForms] = useState<PhoenixFormListItem[]>();
  const markdownEditorProps = useMarkdownEditor(topicMarkdown, topicMarkup);
  const dialogContext = useContext<DialogProviderValue>(DialogContext);

  const editingTopic = useRef<TopicResponse>({ category: "", description: "", name: "" });

  const refreshTopicMarkdown = () => {
    setTopicMarkdown(null);
    retrieveTopicMarkdown(phoenixProjectId, topic.topicId, markdownResponse => setTopicMarkdown(markdownResponse.markdown), mounted);
  };

  const refreshTopicMarkup = () => {
    setTopicMarkup(null);
    retrieveTopicMarkup(topic.topicId, markupResponse => setTopicMarkup(markupResponse.markup), mounted);
  };

  useEffect(() => {
    if (!isCreating) {
      refreshTopicMarkdown();
      refreshTopicMarkup();
      cancelMarkdownEditing();
      setReferencedForms(null);
      listFormsUsingTopic(
        phoenixProjectId,
        topic.topicId,
        topic.uniqueKey,
        formIdsOrNames => setReferencedForms(formsList.filter(f => formIdsOrNames.includes(f.phoenixFormId) || formIdsOrNames.includes(f.name))), mounted);
    }
  }, [topic?.topicId]);

  useEffect(() => () => { mounted.current = false; }, []);

  let topicForCard = isCreating ? editingTopic.current : topic;

  const onPublish = (password: string = null) => {
    if (password === "") return false; // dont let the dialog close

    if (password?.length > 0) {
      setIsSaving(true);

      const onPublishFinished = () => {
        setIsSaving(false);
        refreshList();
      };

      promoteTopic(phoenixProjectId, topic.topicId, { password }, onPublishFinished, onPublishFinished, mounted);
    } else {
      dialogContext.prompt({ title: `Publishing ${topicForCard.name}`, message: "Please enter the publish password", onOkClicked: password => onPublish(password) });
    }
  };

  const onDelete = () => {
    deleteTopic(phoenixProjectId, topic.topicId, (resp: SimpleResult) => {
      if (resp.error) {

        const dialogContent = (
          <div>
            {
              resp.message.split("\n").map((text, index) => (
                <p key={index}>{text}</p>
              ))
            }
          </div>
        );

        dialogContext.showOk("Not Deleted", dialogContent);
      }
      else {
        makeToast("Topic Deleted", "success");
        refreshList();
      }
    }, mounted);
  };

  let contentInformationFields: MCardField[] = [
    { key: "uniqueKey", label: "Code", value: topicForCard.uniqueKey, editable: false },
    { key: "name", label: "Name", value: topicForCard.name, validation: yup.string().required("Name is required") },
    { key: "category", label: "Category", value: topicForCard.category, validation: yup.string().required("Category is required") },
    { key: "description", label: "Description", value: topicForCard.description, validation: yup.string().required("Description is required") },
    { key: "createDate", label: "Create Date", value: topicForCard.createDate, dataType: DATA_TYPE_DATE_TIME, editable: false },
    { key: "modifiedDate", label: "Last Updated", value: topicForCard.modifiedDate, dataType: DATA_TYPE_DATE_TIME, editable: false },
    topicForCard.publishDate && { key: "publishDate", label: "Published Date", value: topicForCard.publishDate, dataType: DATA_TYPE_DATE_TIME, editable: false },
  ];
  contentInformationFields = contentInformationFields.filter(v => v);

  if (!isCreating && canEditContent) {
    contentInformationFields.push({
      key: "buttons",
      control: <>
        <br />
        <Button onClick={onPublish} style={{ marginRight: "10px" }}>Publish</Button>
        <Button onClick={onDelete}>Delete</Button>
      </>,
      hideInEditMode: true });
  }

  if (isCreating) contentInformationFields = contentInformationFields.filter(f => f.editable !== false); // editable === undefined means we can edit

  const editChanged = (changeArray: KeyValueOriginal[]) => {
    changeArray.forEach(c => editingTopic.current[c.key] = c.value);
  };

  const onEditFinished = (changeArray: KeyValueOriginal[]) => {
    let updatedTopic = { ...topic };
    changeArray.forEach(c => updatedTopic[c.key] = c.value);

    let updateTopicRequest: TopicRequest = {
      category: updatedTopic.category,
      description: updatedTopic.description,
      name: updatedTopic.name,
      uniqueKey: updatedTopic.uniqueKey,
    };

    setIsSaving(true);
    updateTopic(phoenixProjectId, topic.topicId, updateTopicRequest, () => {
      refreshList();
      setIsSaving(false);
    }, mounted);
  };

  const onSave = () => {
    if (creatingControlMethods.current.runValidation(true)) {
      let createTopicRequest: TopicRequest = {
        category: editingTopic.current.category,
        description: editingTopic.current.description,
        name: editingTopic.current.name,
        uniqueKey: getUniqueKey(editingTopic.current.name),
      };

      setIsSaving(true);
      createTopic(phoenixProjectId, createTopicRequest, (response: TopicResponse) => {
        refreshList();
        onCreateFinished(response);
        setIsSaving(false);
      }, mounted);
    }
  };

  const onMarkdownStartEdit = () => {
    onMarkdownEditChanged(true);
    markdownEditorProps.changeEditing("startEditing");
  };

  const onToggleHelp = () => markdownEditorProps.setIsShowingHelp(!markdownEditorProps.isShowingHelp);

  const onMarkdownSave = () => {
    setIsSaving(true);
    onMarkdownEditChanged(false);
    updateTopicMarkdown(phoenixProjectId, topic.topicId, markdownEditorProps.markdown, () => {
      markdownEditorProps.changeEditing("save");
      setTopicMarkdown(markdownEditorProps.markdown);
      refreshTopicMarkup();
      refreshList(); // gotta refresh the list here so the date updated gets changed
      setIsSaving(false);
    }, mounted);
  };

  const cancelMarkdownEditing = () => {
    onMarkdownEditChanged(false);
    markdownEditorProps.changeEditing("cancel");
  };

  let contentEditorActions: MCardAction[];

  if (canEditContent) {
    if (markdownEditorProps.isEditing) {
      contentEditorActions = [
        { icon: "check", onClick: onMarkdownSave },
        { icon: "close", onClick: cancelMarkdownEditing },
        { icon: "help", onClick: onToggleHelp },
      ];
    } else {
      contentEditorActions = [{ icon: "edit", onClick: onMarkdownStartEdit }];
    }
  }


  const gridColDefs: MGridColumnDefinition<PhoenixFormListItem>[] = [
    { key: "name", name: "Form Name", dataSource: "name" },
    { key: "publishDate", name: "Publish Date", dataSource: "publishDate", width: 150, dataType: DATA_TYPE_DATETIME_WITHDOW },
    { key: "volume30", name: "30 Day Vol", dataSource: "volume30", width: 100, dataType: DATA_TYPE_NUMBER },
  ];

  return (
    <div className="content-pane">
      {isSaving && <LoadingCover loadingMessages={["Saving Content..."]} />}
      <MCard
        key={topic?.uniqueKey ?? "creating-topic"} // key the card so it hard refreshes when we change topics (resets stuff like if we are editing)
        title="Content Information"
        fields={contentInformationFields}
        isEditable={canEditContent}
        onEditFinished={!isCreating && onEditFinished}
        onlyEditable={isCreating}
        onEditChanged={isCreating && editChanged}
        getControlMethods={cm => creatingControlMethods.current = cm}
        actions={isCreating && [
          { icon: "check", onClick: onSave },
        ]}
      />
      <MGrid
        headerText="Forms Using Content"
        cardStyle
        className="content-forms"
        isFetchingData={!referencedForms}
        columnDefinitions={gridColDefs}
        data={referencedForms}
      />
      {!isCreating &&
      <>
        {canEditContent && <MCard title="Content File Manager">
          <ResourceManager resources={resources} refreshResources={refreshResources} />
        </MCard>}
        <MCard
          title="Content Editor"
          actions={contentEditorActions}
        >
          <MarkdownEditor {...markdownEditorProps} />
        </MCard>
      </>
      }
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  canEditContent: userHasRight(CONTENT_LIBRARY_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentPane);
